import { Editor } from "@tiptap/core";
import { Node as ProseMirrorNode } from "@tiptap/pm/model";

import { ContentNodeType } from "./editor-config-context";
import ContentNodeDefinitions from "./Nodes/definitions";
import { TagMentionAttrs } from "./Nodes/Tag/types";

import { EntityType } from "@/queries/folder-entities/types";

export const regexToHTML = (base: string, disabledNodes?: ContentNodeType[]) => {
  let result = base;

  // Split by newlines and process each part
  const parts = result.split("\n");
  result = parts
    .map(part => {
      // Don't wrap if it's already a block element (starts with <)
      if (part.trim().startsWith("<")) {
        return part;
      }
      return part.trim() ? `<p>${part}</p>` : "<p></p>";
    })
    .join("");

  ContentNodeDefinitions.forEach(({ nodeType, regex, toXML }) => {
    // Skip processing if this node type is disabled
    if (disabledNodes?.includes(nodeType)) {
      return;
    }
    result = result.replaceAll(regex, (...match) => toXML(match));
  });

  return result;
};

export const serializeToMarkdown = (editor: Editor): string => {
  const { doc } = editor.state;
  let result = "";
  let isFirstParagraph = true;

  function serializeNode(node: ProseMirrorNode): string {
    if (node.type.name === "text") {
      const text = node.text || "";
      return text;
    }

    if (node.type.name === "paragraph") {
      let content = "";
      if (node.content) {
        node.content.forEach((child: ProseMirrorNode) => {
          content += serializeNode(child);
        });
      }
      return content;
    }

    if (node.type.spec.toText) {
      return node.type.spec.toText({ node });
    }

    // Handle nodes with custom renderText methods
    if (node.type.spec.renderText) {
      return node.type.spec.renderText({ node });
    }

    // Default: serialize children
    let content = "";
    if (node.content) {
      node.content.forEach((child: ProseMirrorNode) => {
        content += serializeNode(child);
      });
    }
    return content;
  }

  if (doc.content) {
    doc.content.forEach((node: ProseMirrorNode) => {
      const serialized = serializeNode(node);

      if (node.type.name === "paragraph") {
        // Add double newline before paragraphs (except the first one)
        if (!isFirstParagraph) {
          result += "\n";
        }
        isFirstParagraph = false;
        result += serialized;
      } else {
        // For other node types, just append
        result += serialized;
      }
    });
  }

  return result;
};

export function escapeXml(unsafe: string): string {
  if (!unsafe) {
    return "";
  }

  const str = String(unsafe);

  let result = "";
  const lastIndex = 0;

  const entityMap: Record<string, string> = {
    "<": "&lt;",
    ">": "&gt;",
    "&": "&amp;",
    "'": "&apos;",
    '"': "&quot;",
  };

  // Regex to match HTML entities
  const entityRegex = /^&[a-zA-Z0-9#][a-zA-Z0-9#]*;$/;

  /**
   * Helper function to escape XML special characters while preserving existing entities
   */
  function escapePart(text: string): string {
    return text.replace(/&[a-zA-Z0-9#][a-zA-Z0-9#]*;|[<>&'"]/g, match =>
      entityRegex.test(match) ? match : entityMap[match] || match,
    );
  }

  // Escape any remaining text after all widget matches
  result += escapePart(str.substring(lastIndex));

  return result;
}

export function unescapeXml(escaped: string): string {
  if (!escaped) {
    return "";
  }

  return escaped
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&amp;/g, "&")
    .replace(/&apos;/g, "'")
    .replace(/&quot;/g, '"');
}

export function showHiddenCharacters(str: string): string {
  if (!str) {
    return "";
  }

  return str
    .replace(/\n/g, "\\n")
    .replace(/\r/g, "\\r")
    .replace(/\t/g, "\\t")
    .replace(/\f/g, "\\f")
    .replace(/\v/g, "\\v")
    .replace(/\0/g, "\\0")
    .replace(/\b/g, "")
    .replace(/\u00A0/g, "\\u00A0") // non-breaking space
    .replace(/\u2028/g, "\\u2028") // line separator
    .replace(/\u2029/g, "\\u2029") // paragraph separator
    .replace(
      /[\u200B-\u200D\uFEFF]/g,
      match =>
        // zero-width characters
        `\\u${match.charCodeAt(0).toString(16).toUpperCase().padStart(4, "0")}`,
    );
}

export const parseTagString = (tagContent: string): TagMentionAttrs => {
  const parts = tagContent.split(":");
  const [tagType, id, name, email, entityType, link] = parts;
  const parsedId = id && /^\d+$/.test(id) ? parseInt(id, 10) : id || undefined;
  return {
    tagType: (tagType || "user") as "user" | "entity" | "entityType",
    id: parsedId,
    name: name || "",
    email: email || undefined,
    entityType: entityType as EntityType | undefined,
    link: link || undefined,
  };
};
